import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, ArrowRight, CheckCircle2, Compass } from 'lucide-react';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';
import { trackTelemetryEvent } from '../lib/telemetry';

export default function ModeSelection() {
  const navigate = useNavigate();

  const handleRelationshipMode = () => {
    localStorage.setItem('tension_mode', 'relationship');
    void trackTelemetryEvent('mode_selected', { mode: 'relationship', source: 'mode_selection' });
    window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
    navigate('/onboarding', { replace: true });
  };

  const handleSinglesMode = () => {
    localStorage.setItem('tension_mode', 'single');
    void trackTelemetryEvent('mode_selected', { mode: 'single', source: 'mode_selection' });
    window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
    navigate('/singles-onboarding', { replace: true });
  };

  return (
    <div className="min-h-screen tension-page-bg flex items-center justify-center px-6 py-12">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <header className="text-center space-y-3" role="banner">
          <h1 className="text-5xl font-light tracking-wide text-neutral-900">TENSION</h1>
          <p className="text-xl text-neutral-600">
            Understand relationships before pressure breaks the bond
          </p>
          <p className="text-sm text-neutral-500">Choose your journey</p>
        </header>

        <div className="mx-auto grid w-full max-w-5xl gap-6 md:grid-cols-2">
          <Card
            className="p-8 space-y-6 hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-violet-300"
            onClick={handleRelationshipMode}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" />
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-light text-neutral-900">
                I'm in a Relationship
              </h2>
              <p className="text-neutral-600">
                Track tension, forecast patterns, and strengthen your existing relationship
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-neutral-900">Perfect for:</p>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Couples who want to prevent conflicts before they happen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Understanding and reducing relationship tension</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Daily check-ins with tension scoring (RTI) and forecasting</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Pattern analysis and personalized action cards</span>
                </li>
              </ul>
            </div>

            <Button className="w-full" size="lg">
              Continue with Relationship Mode
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>

          <Card
            className="p-8 space-y-6 hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-blue-300"
            onClick={handleSinglesMode}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
              <Compass className="w-8 h-8 text-white" />
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-light text-neutral-900">
                I'm Single
              </h2>
              <p className="text-neutral-600">
                Build your relationship-readiness profile, improve connection choices, and transition cleanly when a relationship starts
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-neutral-900">Perfect for:</p>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Clarifying what a healthy partner dynamic looks like for you</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Using alignment and readiness tools before commitment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Tracking interaction patterns and green/red flags over time</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Moving into relationship mode with preserved insights</span>
                </li>
              </ul>
            </div>

            <Button className="w-full" size="lg">
              Continue with Singles Mode
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        </div>

        {/* Footer Note */}
        <div className="text-center space-y-2">
          <p className="text-xs text-neutral-500">
            🔒 Relationship data stays on your device by default (TLS for sign-in and sync)
          </p>
        </div>
      </div>
    </div>
  );
}